import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Wallet, DollarSign, TrendingUp, ArrowDownCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TipsterWallet = () => {
  const { user } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWallet();
  }, []);

  // Auto-populate PayPal email when modal opens or payment method changes
  useEffect(() => {
    if (showWithdrawModal && paymentMethod === 'paypal' && user?.paypal_email) {
      setPaymentDetails(user.paypal_email);
    } else if (paymentMethod !== 'paypal') {
      setPaymentDetails('');
    }
  }, [showWithdrawModal, paymentMethod, user]);

  const fetchWallet = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tipster/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    
    if (!amount || amount < 50) {
      toast.error('Minimum withdrawal is €50');
      return;
    }
    
    if (amount > wallet.available_balance) {
      toast.error('Insufficient balance');
      return;
    }
    
    if (!paymentDetails) {
      toast.error('Please provide payment details');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/tipster/withdraw`,
        {
          amount: amount,
          payment_method: paymentMethod,
          payment_details: paymentDetails
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Withdrawal request submitted! Admin will process it soon.');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setPaymentDetails('');
      fetchWallet();
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8">
          <div className="container mx-auto px-4">
            <div className="text-white text-center py-8">Loading wallet...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Wallet className="w-8 h-8 text-green-400" />
              My Wallet
            </h1>
          </div>

          {/* Platform Fee Info */}
          <Card className="glass-dark border-yellow-500/30 bg-yellow-900/10 mb-6">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-semibold">Platform Fee: {wallet?.platform_fee_percentage || 20}%</p>
                <p className="text-gray-300 text-sm mt-1">
                  A {wallet?.platform_fee_percentage || 20}% platform fee is applied to all tip sales and subscriptions. 
                  Minimum withdrawal amount is €50.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Wallet Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Total Earnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-white">
                  <Price amount={wallet?.total_earnings || 0} />
                </p>
                <p className="text-gray-400 text-sm mt-2">After platform fee deduction</p>
              </CardContent>
            </Card>

            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Available Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-green-400">
                  <Price amount={wallet?.available_balance || 0} />
                </p>
                <p className="text-gray-400 text-sm mt-2">Ready to withdraw</p>
              </CardContent>
            </Card>

            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <ArrowDownCircle className="w-5 h-5 text-yellow-400" />
                  Pending Withdrawal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-yellow-400">
                  <Price amount={wallet?.pending_withdrawal_amount || 0} />
                </p>
                <p className="text-gray-400 text-sm mt-2">Awaiting admin approval</p>
              </CardContent>
            </Card>

            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Total Withdrawn
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-purple-400">
                  <Price amount={wallet?.withdrawn_amount || 0} />
                </p>
                <p className="text-gray-400 text-sm mt-2">All time withdrawals</p>
              </CardContent>
            </Card>
          </div>

          {/* Withdraw Button */}
          <Card className="glass-dark border-white/10">
            <CardContent className="p-6">
              <Button
                onClick={() => setShowWithdrawModal(true)}
                disabled={!wallet?.can_withdraw}
                className="bg-green-600 hover:bg-green-700 w-full text-lg py-6"
              >
                <ArrowDownCircle className="w-5 h-5 mr-2" />
                Withdraw Earnings
              </Button>
              {!wallet?.can_withdraw && (
                <p className="text-gray-400 text-sm text-center mt-3">
                  Minimum <Price amount={wallet?.minimum_withdrawal || 50} /> required to withdraw. 
                  Current balance: <Price amount={wallet?.available_balance || 0} />
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span>Request Withdrawal</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowWithdrawModal(false)}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white">Amount (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="50"
                  max={wallet?.available_balance}
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Minimum €50"
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-gray-400 text-xs mt-1">
                  Available: <Price amount={wallet?.available_balance || 0} />
                </p>
              </div>

              <div>
                <Label className="text-white">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white">Payment Details</Label>
                <Textarea
                  value={paymentDetails}
                  onChange={(e) => setPaymentDetails(e.target.value)}
                  placeholder={paymentMethod === 'paypal' ? 'PayPal email address' : paymentMethod === 'bank_transfer' ? 'Bank account details (IBAN, etc.)' : 'Payment details'}
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
                {paymentMethod === 'paypal' && user?.paypal_email && (
                  <p className="text-xs text-green-400 mt-1">
                    ✓ Using PayPal email from your profile settings
                  </p>
                )}
                {paymentMethod === 'paypal' && !user?.paypal_email && (
                  <p className="text-xs text-yellow-400 mt-1">
                    💡 Tip: Add your PayPal email in Profile → User Settings to auto-fill this field
                  </p>
                )}
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3">
                <p className="text-blue-400 text-sm">
                  <strong>Note:</strong> Your withdrawal request will be reviewed by admin. 
                  Processing typically takes 1-3 business days.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleWithdraw}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button
                  onClick={() => setShowWithdrawModal(false)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default TipsterWallet;